import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, User, Award, Loader2, AlertCircle, X } from "lucide-react";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { SpotIncentive } from "@/bryntum-lib/models/SpotIncentive";
import { toast } from "sonner";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys";
import { Resource } from "@/bryntum-lib/models/Resource";

interface PointChange {
  id: string;
  change: number;
  description: string;
  isReleased: boolean;
  incentiveId: number;
  resources: Resource[];
}

export const IncentivePointsSection = ({ compact = false }: { compact?: boolean }) => {
  const { file, shouldShowSpotIncentiveButton } = useFileDetailModal();
  const { project } = useSwivuBryntumContext();
  const [isReleasing, setIsReleasing] = useState(false);
  const [archivingRecordId, setArchivingRecordId] = useState<number | null>(null);

  // Subscribe to store changes for reactive updates
  const { subscribe: subscribeToSpotIncentive, version } = useSwivuStore<SpotIncentive>({ storeId: STORE_KEYS.spotIncentiveStore });

  // Check if user has permission to archive
  const canArchive = useMemo(() => {
    return checkIncentivePermission({
      project,
      permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS
    });
  }, [project]);

  // Get spot incentives for the current file (excluding archived)
  const spotIncentives = useMemo(() => {
    if (!project || !file?.id) return [];
    const store = project.spotIncentiveStore;
    if (!store) return [];
    return store.query((incentive: SpotIncentive) =>
      incentive.fileId === file.id && !incentive.archived
    ) as SpotIncentive[];
  }, [project, file?.id, version]);

  // Check if there are any unreleased spot incentives for this file
  const unreleasedIncentivesCount = useMemo(() => {
    if (!project || !file?.id) return 0;
    const store = project.spotIncentiveStore;
    if (!store) return 0;
    const unreleasedIncentives = store.query((incentive: SpotIncentive) =>
      incentive.fileId === file.id && !incentive.isReleased && !incentive.archived
    ) as SpotIncentive[];
    return unreleasedIncentives.length;
  }, [project, file?.id, version]);

  const hasUnreleasedIncentives = unreleasedIncentivesCount > 0;

  // Map spot incentives to PointChange interface
  const pointChanges = useMemo<PointChange[]>(() => {
    if (!project || !spotIncentives.length) return [];

    const changes = spotIncentives.map((incentive: SpotIncentive) => {
      const resources = (incentive.resources as Resource[]) || [];

      // Calculate total points from positive tags
      const positivePoints = (incentive.positiveTagIds || []).reduce((sum: number, tagId: number) => {
        const tag = project.incentiveTagsStore?.getById(tagId);
        const points = tag?.categoryRecord?.points || 0;
        return sum + points;
      }, 0);

      // Calculate total points from negative tags
      const negativePoints = (incentive.negativeTagIds || []).reduce((sum: number, tagId: number) => {
        const tag = project.incentiveTagsStore?.getById(tagId);
        const points = tag?.categoryRecord?.points || 0;
        return sum + points;
      }, 0);

      // Calculate net change for this incentive
      const netChange = positivePoints - negativePoints;

      return {
        id: `incentive-${incentive.id}`,
        change: netChange,
        description: incentive.name || "",
        isReleased: incentive.isReleased,
        incentiveId: incentive.id,
        resources,
      };
    });

    // Sort: pending (not released) records first, then released records
    return changes.sort((a, b) => {
      if (a.isReleased === b.isReleased) return 0;
      return a.isReleased ? 1 : -1;
    });
  }, [spotIncentives, project]);

  // Calculate totals
  const netChange = useMemo(() => {
    return pointChanges.reduce((sum, change) => sum + change.change, 0);
  }, [pointChanges]);


  useEffect(() => {
    return subscribeToSpotIncentive(({ record: changedRecord }) => {
      return changedRecord.fileId === file?.id;
    });
  }, [subscribeToSpotIncentive, file]);

  const handleArchiveIncentive = async (incentiveId: number) => {
    if (!project?.spotIncentiveStore || !canArchive) return;

    setArchivingRecordId(incentiveId);
    try {
      const incentive = project.spotIncentiveStore.getById(incentiveId);
      if (incentive) {
        incentive.archived = true;
        await project.sync();
        toast.success("Point record removed");
      }
    } catch (error) {
      console.error("Failed to archive point:", error);
      toast.error("Failed to remove point record");
    } finally {
      setArchivingRecordId(null);
    }
  };

  // Starting points would need to come from a different source (e.g., task or resource baseline)
  // For now, we'll show net change and final points as net change
  const startingPoints = 0; // This would need to be calculated from baseline points
  const finalPoints = startingPoints + netChange;
  const hasNoPoints = finalPoints === 0 && pointChanges.length === 0;

  // Show empty state when there are no points
  if (hasNoPoints) {
    return (
      <div
        className={
          compact
            ? "flex w-full items-center justify-center overflow-hidden py-8"
            : "flex h-full w-full items-center justify-center overflow-hidden p-6"
        }
      >
        <div
          className={
            compact
              ? "mx-auto flex w-full max-w-md flex-col items-center gap-4 rounded-xl border border-dashed border-border bg-muted/50 px-6 py-8 text-center"
              : "mx-auto flex w-full max-w-md flex-col items-center gap-6 rounded-xl border border-dashed border-border bg-muted/50 px-8 py-10 text-center"
          }
        >
          <div
            className={
              compact
                ? "mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-primary/5"
                : "mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-primary/5"
            }
          >
            <Award className={compact ? "size-6" : "size-7"} />
          </div>
          <div className={compact ? "space-y-1.5" : "space-y-2"}>
            <h3
              className={
                compact
                  ? "text-base font-normal text-foreground"
                  : "text-lg font-semibold text-foreground"
              }
            >
              No points assigned yet
            </h3>
            <p className="max-w-sm text-sm font-normal leading-relaxed text-muted-foreground">
              Award or deduct points for resources.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "flex flex-col overflow-visible" : "flex h-full min-h-0 flex-col overflow-hidden"}>
      {/* Scrollable Content Area */}
      <div
        className={
          compact
            ? "space-y-3 overflow-visible p-0.5 pb-2"
            : "min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        }
      >
        {/* Unreleased Incentives Notice */}
        {hasUnreleasedIncentives && (
          <div
            className={
              compact
                ? "flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2"
                : "flex items-start gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3"
            }
          >
            <AlertCircle
              className={
                compact
                  ? "mt-0.5 h-4 w-4 shrink-0 text-primary"
                  : "mt-0.5 h-5 w-5 shrink-0 text-primary"
              }
            />
            <div className="min-w-0 flex-1">
              <p className={compact ? "text-sm font-normal text-foreground" : "text-sm font-medium text-foreground"}>
                {unreleasedIncentivesCount}{" "}
                {unreleasedIncentivesCount === 1 ? "record needs" : "records need"} to be released
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Click &quot;Release&quot; below to release{" "}
                {unreleasedIncentivesCount === 1 ? "this point" : "these points"}
              </p>
            </div>
          </div>
        )}

        {/* Transaction List */}
        {pointChanges.length > 0 && (
          <div className={compact ? "space-y-2" : "space-y-2.5"}>
            {pointChanges.map((change) => {
              const allUserNames = change.resources.flatMap(
                (resource) => resource.eligibleUserValues || [],
              );
              const uniqueUserNames = Array.from(new Set(allUserNames));
              const amountClass =
                change.change > 0
                  ? "text-green-700 dark:text-green-400"
                  : change.change < 0
                    ? "text-destructive"
                    : "text-muted-foreground";

              return (
                <div
                  key={change.id}
                  className={
                    compact
                      ? "group flex items-start gap-2.5 rounded-md border border-border/60 bg-muted/40 p-3 transition-colors hover:bg-muted/70"
                      : "group flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-3.5 transition-all duration-200 hover:bg-muted"
                  }
                >
                  <div
                    className={`flex shrink-0 items-center justify-center rounded-full ${
                      compact ? "h-7 w-7" : "h-8 w-8"
                    } ${
                      change.change > 0
                        ? "bg-green-600/10 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                        : "bg-destructive/10 text-destructive"
                    }`}
                  >
                    {change.change > 0 ? (
                      <ArrowUp className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
                    ) : (
                      <ArrowDown className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
                    )}
                  </div>
                  <div className={`min-w-0 flex-1 ${compact ? "space-y-1" : "space-y-1.5"}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`tabular-nums ${
                          compact ? "text-sm font-semibold" : "text-base font-bold"
                        } ${amountClass}`}
                      >
                        {change.change > 0 ? `+${change.change}` : change.change}
                      </span>
                      <span
                        className={`min-w-0 flex-1 truncate text-sm text-foreground ${
                          compact ? "font-normal" : "font-medium"
                        }`}
                      >
                        {change.description}
                      </span>
                      {change.isReleased ? (
                        <span
                          className={
                            compact
                              ? "inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/60 px-1.5 py-0.5 text-[0.6875rem] font-normal text-muted-foreground"
                              : "inline-flex items-center gap-1 rounded-full border border-green-600/20 bg-green-600/10 px-2 py-0.5 text-xs font-medium text-green-700 dark:border-green-500/20 dark:bg-green-500/15 dark:text-green-400"
                          }
                        >
                          {!compact && (
                            <span className="h-1.5 w-1.5 rounded-full bg-green-600 dark:bg-green-500" />
                          )}
                          Released
                        </span>
                      ) : (
                        <span
                          className={
                            compact
                              ? "inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[0.6875rem] font-normal text-primary"
                              : "inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                          }
                        >
                          {!compact && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="h-3 w-3 shrink-0" />
                      {uniqueUserNames.length > 0 ? (
                        <span className="truncate">{uniqueUserNames.join(", ")}</span>
                      ) : (
                        <span className="text-muted-foreground/60">No resources assigned</span>
                      )}
                    </div>
                  </div>
                  {canArchive && !change.isReleased && (
                    <button
                      onClick={() => handleArchiveIncentive(change.incentiveId)}
                      disabled={archivingRecordId === change.incentiveId}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Remove point record"
                    >
                      {archivingRecordId === change.incentiveId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      {shouldShowSpotIncentiveButton && hasUnreleasedIncentives && (
        <div className="shrink-0 bg-card">
          <div
            className={
              compact
                ? "border-t border-border/60 px-0.5 pb-1 pt-2.5"
                : "border-t border-border px-6 pb-4 pt-3"
            }
          >
            <div className="flex items-center justify-end">
              <Button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsReleasing(true);
                  try {
                    if (project?.spotIncentiveStore && file?.id) {
                      const unreleasedIncentives = project.spotIncentiveStore.query(
                        (incentive: SpotIncentive) =>
                          incentive.fileId === file.id && !incentive.isReleased,
                      ) as SpotIncentive[];

                      if (unreleasedIncentives.length > 0) {
                        unreleasedIncentives.forEach((incentive) => {
                          incentive.isReleased = true;
                        });
                        await project.sync();
                        toast.success("Points adjusted are now visible on the Points surface.");
                      }
                    }
                  } finally {
                    setIsReleasing(false);
                  }
                }}
                disabled={isReleasing}
                className="inline-flex items-center gap-2"
                variant="default"
                size="sm"
              >
                {isReleasing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Releasing points...
                  </>
                ) : (
                  "Release"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
